//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'

import { ERC20Wei } from '@shared/conversions'

import { logOT } from '@shared/loggingShared'

import {
  PoolAccountCTO,
  PoolBaseAccountCTO,
  PoolDynamicAccountCTO,
  PoolDynamicOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexOverviewCTO,
  PoolOverviewCTO,
} from '@shared/sharedTypes'
import { Dayjs } from 'dayjs'

const PREFIX = 'pools'

// Define a type for the slice state
export interface PoolState {
  isInitialized: boolean
  actionEvent: string | undefined
  poolOverviewCTOs: Record<EthAddr, PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>

  poolAccountCTOs: Record<EthAddr, Record<EthAddr, PoolBaseAccountCTO>>

  accountPoolPairs: Record<EthAddr, Record<EthAddr, PoolAccountAccessPair>>
  lastEvent: string
  lastBlockNumber: number
}

// Define the initial state using that type
export const initialPoolState: PoolState = {
  isInitialized: false,
  actionEvent: undefined,
  poolOverviewCTOs: {},
  poolAccountCTOs: {},
  accountPoolPairs: {},
  lastEvent: '',
  lastBlockNumber: 0,
}

export const poolSlice = createSlice({
  name: PREFIX,
  initialState: initialPoolState,
  reducers: {
    addPoolAccountAccessPairs: (
      poolState: Draft<PoolState>,
      action: PayloadAction<{
        poolAccountAccessPairs: PoolAccountAccessPair[]
      }>
    ) => {
      for (const poolAccountAccessPair of action.payload.poolAccountAccessPairs) {
        if (!poolState.accountPoolPairs[poolAccountAccessPair.accountAddr]) {
          poolState.accountPoolPairs[poolAccountAccessPair.accountAddr] = {}
        }
        poolState.accountPoolPairs[poolAccountAccessPair.accountAddr][poolAccountAccessPair.poolAddr] =
          poolAccountAccessPair
      }
    },

    clearActionEvent: (poolState: Draft<PoolState>, _action: PayloadAction<void>) => {
      poolState.actionEvent = undefined
    },

    clearPoolState: (poolState: Draft<PoolState>, _action: PayloadAction<void>) => {
      poolState.isInitialized = false
      poolState.actionEvent = undefined
      poolState.poolOverviewCTOs = {}
      poolState.poolAccountCTOs = {}
      poolState.lastEvent = ''
      poolState.lastBlockNumber = 0
      poolState.accountPoolPairs = {}
      logOT.info('Pool state cleared')
    },
    creatPoolSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},
    creatPoolFail: (_poolState: Draft<PoolState>, _action: PayloadAction<void>) => {},

    activatePoolSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},

    transferRepayFlexWithdrawalSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; eventId: string; requestedAssets: ERC20Wei }>
    ) => {},
    transferRepayFlexWithdrawalFail: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; eventId: string; requestedAssets: ERC20Wei }>
    ) => {},

    releaseFlexWithdrawalSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; eventId: string; requestedAssets: ERC20Wei }>
    ) => {},
    releaseFlexWithdrawalFail: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; eventId: string; requestedAssets: ERC20Wei }>
    ) => {},

    dailyAccrualFlexSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},
    dailyAccrualFlexFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},

    setFlexInterestRateSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},
    setFlexInterestRateFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},

    depositToPoolSuccess: (
      poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; amountWei: ERC20Wei }>
    ) => {
      poolState.actionEvent = poolActions.depositToPoolSuccess.type
    },
    approveDepositToPoolSuccess: (
      poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; amountWei: ERC20Wei }>
    ) => {
      poolState.actionEvent = poolActions.approveDepositToPoolSuccess.type
    },
    depositToPoolFail: (
      poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; amountWei: ERC20Wei }>
    ) => {
      poolState.actionEvent = poolActions.depositToPoolFail.type
    },

    completeRolloverNetPaymentSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr }>
    ) => {},
    completeRolloverNetPaymentFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{ poolAddr: EthAddr }>) => {},

    withdrawFeesSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {
      return undefined
    },
    withdrawFeesFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    redeemFromPoolSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    redeemRequestFromPoolSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    requestEarlyRedeemSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    requestEarlyRedeemFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    setExchangeRateSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    setExchangeRateFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    setIndicativeInterestRateSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    setIndicativeInterestRateFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    acceptEarlyRedeemRequestSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    acceptEarlyRedeemRequestFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    repayEarlyWithdrawSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    repayEarlyWithdrawFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    setOffchainDepositSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    setOffchainDepositFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    changeWithdrawDestinationSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    changeWithdrawDestinationFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    repayDynamicWithdrawSuccess: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},
    repayDynamicWithdrawFail: (_poolState: Draft<PoolState>, _action: PayloadAction<{}>) => {},

    updatePoolAccountCTO: (
      poolState: Draft<PoolState>,
      action: PayloadAction<PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO>
    ) => {
      logOT.trace('updatePoolAccountCTO', action.payload.poolAddr, action.payload)
      if (!poolState.poolAccountCTOs[action.payload.poolAddr]) {
        const added: Record<EthAddr, Record<EthAddr, PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO>> = {}
        added[action.payload.poolAddr] = {}
        added[action.payload.poolAddr][action.payload.accountAddr] = action.payload

        poolState.poolAccountCTOs = {
          ...poolState.poolAccountCTOs,
          ...added,
        }
      } else {
        poolState.poolAccountCTOs[action.payload.poolAddr][action.payload.accountAddr] = action.payload
        const added: Record<EthAddr, PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO> = {}
        added[action.payload.accountAddr] = action.payload

        poolState.poolAccountCTOs[action.payload.poolAddr] = {
          ...poolState.poolAccountCTOs[action.payload.poolAddr],
          ...added,
        }
      }
      logOT.trace('updatedPoolState', poolState)
    },

    setPoolAccountCTOList: (
      poolState: Draft<PoolState>,
      action: PayloadAction<(PoolAccountCTO | PoolFlexAccountCTO | PoolDynamicAccountCTO)[]>
    ) => {
      for (const poolAccountCTO of action.payload) {
        logOT.info('setPoolAccountCTOList', poolAccountCTO.poolAddr, poolAccountCTO.accountAddr, poolAccountCTO)
        if (!poolState.poolAccountCTOs[poolAccountCTO.poolAddr]) {
          poolState.poolAccountCTOs[poolAccountCTO.poolAddr] = {}
        }
        poolState.poolAccountCTOs[poolAccountCTO.poolAddr][poolAccountCTO.accountAddr] = poolAccountCTO
      }
    },

    updatePoolOverviewCTO: (
      poolState: Draft<PoolState>,
      action: PayloadAction<PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO>
    ) => {
      logOT.trace('updatePoolCTO', action.payload.poolAddr, action.payload)
      poolState.poolOverviewCTOs[action.payload.poolAddr] = action.payload
      const added: Record<EthAddr, PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO> = {}
      added[action.payload.poolAddr] = action.payload

      poolState.poolOverviewCTOs = {
        ...poolState.poolOverviewCTOs,
        ...added,
      }

      logOT.trace('updatedPoolState', poolState)
      if (action.payload.blockNumber > poolState.lastBlockNumber) {
        poolState.lastBlockNumber = action.payload.blockNumber
      }
    },

    setPoolContractCTOList: (
      poolState: Draft<PoolState>,
      action: PayloadAction<(PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO)[]>
    ) => {
      logOT.info('setPoolContractCTOList', action.payload)
      for (const poolContractCTO of action.payload) {
        poolState.poolOverviewCTOs[poolContractCTO.poolAddr] = poolContractCTO
      }
    },

    finishedInitializing: (poolState: Draft<PoolState>, _action: PayloadAction) => {
      logOT.info('FINISHED INITIALIZING')
      poolState.isInitialized = true
    },

    updateNonBusinessDaysSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; nonBusinessDays: Dayjs[] }>
    ) => {},
    updateNonBusinessDaysFail: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; nonBusinessDays: Dayjs[] }>
    ) => {},

    createLoanSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; loanAddr: EthAddr }>
    ) => {},

    approveLoanSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; loanAddr: EthAddr }>
    ) => {},

    initiateRolloverSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{
        poolAddr: EthAddr
        loanAddr: EthAddr
        priorLoanAddr: EthAddr
      }>
    ) => {},

    rolloverFinalizeSuccess: (
      _poolState: Draft<PoolState>,
      _action: PayloadAction<{ poolAddr: EthAddr; loanAddr: EthAddr }>
    ) => {},
  },
})

export const poolActions = { ...poolSlice.actions }

export const poolReducer = poolSlice.reducer
export default poolSlice
